import { useEffect, useRef } from 'react'

import { clsx } from 'clsx'

import { useChat, useUnreadChatsCount } from '@tribeplatform/react-sdk/hooks'
import { Chat } from '@tribeplatform/react-sdk/types'
import { Sidebar } from '@tribeplatform/react-ui-kit/Sidebar'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'

import { useQueryParam } from '../../common/index.js'
import { T, useI18n } from '../../i18n/index.js'

import { ChatList } from './ChatList.js'
import { MessagingSidebarHeader } from './Header.js'

export enum MessagingSidebarTabs {
  Chats = 'chats',
  ArchivedChats = 'archived-chats',
}

interface Props {
  chatId: Chat['_id']
}

export const MessagingSidebar = ({ chatId }: Props) => {
  const { $t } = useI18n()
  const [activeTabParam, setActiveTabParam] = useQueryParam(
    { name: 'tab' },
    {
      navigationMode: 'replace',
    },
  )
  const { data: chat } = useChat({
    variables: { id: chatId },
    useQueryOptions: {
      enabled: !!chatId,
    },
  })
  const { data: unreadChatsCount } = useUnreadChatsCount()
  const unreadMessagesCount = unreadChatsCount?.active.unreadChatsCount ?? 0
  const archivedUnreadMessagesCount =
    unreadChatsCount?.archived.unreadChatsCount ?? 0
  const unreadCountMessage = unreadMessagesCount
    ? `${unreadMessagesCount}`
    : undefined
  const archivedUnreadCountMessage = archivedUnreadMessagesCount
    ? `${archivedUnreadMessagesCount}`
    : undefined
  const isArchivedTabActive =
    activeTabParam === MessagingSidebarTabs.ArchivedChats
  const isArchived = !!chat?.currentParticipant?.archivedAt
  const hasActiveTabQuery =
    activeTabParam === MessagingSidebarTabs.Chats ||
    activeTabParam === MessagingSidebarTabs.ArchivedChats
  const shouldChangeTabToArchived = isArchived && !isArchivedTabActive
  const tabAdjusted = useRef(hasActiveTabQuery && !shouldChangeTabToArchived)
  // Since tabs are tied to query params, we need to navigate to the correct tab
  // when the user visits the link without any query params.
  useEffect(() => {
    if (!chat || tabAdjusted.current) {
      return
    }

    if (isArchived && !isArchivedTabActive) {
      setActiveTabParam(MessagingSidebarTabs.ArchivedChats)
      tabAdjusted.current = true
    }
  }, [chat, isArchived, isArchivedTabActive, setActiveTabParam])

  return (
    <Sidebar size="auto" className="sm:rounded-s-md">
      <Sidebar.Content className="p-0">
        <Tabs.Group selectedIndex={isArchivedTabActive ? 1 : 0}>
          <div className="sticky top-0 z-10 bg-surface flex flex-col">
            <div className="p-3 border-b border-line-subdued">
              <MessagingSidebarHeader />
            </div>
            <Tabs.List variant="filled" className="p-3">
              <Tabs.Tab
                selected={!isArchivedTabActive}
                className={clsx(isArchivedTabActive && '!font-normal !text-sm')}
                onClick={() => setActiveTabParam(MessagingSidebarTabs.Chats)}
                name={$t({
                  id: 'Generics.Chats',
                  defaultMessage: 'Chats',
                })}
                badge={unreadCountMessage}
              >
                <T id="Generics.Chats" defaultMessage="Chats" />
              </Tabs.Tab>
              <Tabs.Tab
                selected={isArchivedTabActive}
                onClick={() =>
                  setActiveTabParam(MessagingSidebarTabs.ArchivedChats)
                }
                className={clsx(
                  !isArchivedTabActive && '!font-normal !text-sm',
                )}
                name={$t({
                  id: 'Messaging.Sidebar.ArchivedChats',
                  defaultMessage: 'Archived Chats',
                })}
                badge={archivedUnreadCountMessage}
              >
                <T
                  id="Messaging.Sidebar.ArchivedChats"
                  defaultMessage="Archived Chats"
                />
              </Tabs.Tab>
            </Tabs.List>
          </div>
          <Tabs.Panels className="!mt-0 overflow-auto">
            <Tabs.Panel>
              <ChatList currentChatId={chatId} />
            </Tabs.Panel>
            <Tabs.Panel>
              <ChatList archived currentChatId={chatId} />
            </Tabs.Panel>
          </Tabs.Panels>
        </Tabs.Group>
      </Sidebar.Content>
    </Sidebar>
  )
}
